/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/marketsurvey.css";
import { Country, City } from "country-state-city";
import { supabase } from "../supabase";
import { generateWord } from "../utils/wordGenerator.js";

const sections = [
  {
    ar: "بيانات توجيه الدراسة",
    en: "Study Recipient",
    questions: [
      {
        id: "recipient_name",
        labelAr: "الدخل الاسم الذي سيتم توجيه الدراسة اليه",
        labelEn: "Enter the name the study will be addressed to",
        type: "text",
        required: true,
        placeholderAr: "مثال: شركة / اسم شخص / جهة",
        placeholderEn: "Example: Company / Person / Entity",
      },
      {
        id: "project_name",
        labelAr: "اسم المشروع",
        labelEn: "Project Name",
        type: "text",
        required: true,
        placeholderAr: "مثال: مصنع أثاث",
        placeholderEn: "Example: Furniture Factory",
      },
      {
        id: "project_desc",
        labelAr: "وصف مختصر للمشروع",
        labelEn: "Brief Project Description",
        type: "text",
        required: true,
        placeholderAr: "اكتب وصفًا مختصرًا للمشروع",
        placeholderEn: "Write a short project description",
      },
      {
        id: "activity_category",
        labelAr: "فئة النشاط",
        labelEn: "Business Category",
        type: "radio",
        required: true,
        options: [
          ["trade", "تجارة", "Trade"],
          ["services", "خدمات", "Services"],
          ["tech", "تقنية", "Technology"],
          ["industry", "صناعة", "Industry"],
          ["agriculture", "زراعة", "Agriculture"],
          ["logistics", "لوجستيات", "Logistics"],
          ["education", "تعليم", "Education"],
          ["health", "صحة", "Healthcare"],
          ["entertainment", "ترفيه", "Entertainment"],
          ["other", "أخرى", "Other"],
        ],
        detailsId: "activity_other",
        detailsAr: "اكتب فئة النشاط الأخرى",
        detailsEn: "Enter other category",
      },
      {
        id: "target_country",
        labelAr: "الدولة المستهدفة",
        labelEn: "Target Country",
        type: "text",
        required: true,
        placeholderAr: "مثال: المملكة العربية السعودية",
        placeholderEn: "Example: Saudi Arabia",
      },
      {
        id: "target_city",
        labelAr: "المدينة المستهدفة",
        labelEn: "Target City",
        type: "text",
        placeholderAr: "مثال: الرياض",
        placeholderEn: "Example: Riyadh",
      },
      {
        id: "report_language",
        labelAr: "لغة التقرير",
        labelEn: "Report Language",
        type: "radio",
        required: true,
        options: [
          ["ar", "العربية", "Arabic"],
          ["en", "الإنجليزية", "English"],
        ],
      },
    ],
  },
  {
    ar: "أولًا: بيانات عامة عن النشاط الفني",
    en: "1) General Technical Activity Data",
    questions: [
      {
        id: "technical_description",
        labelAr: "1. وصف المنتج أو الخدمة بشكل فني وتطبيقي",
        labelEn: "1. Technical and practical description of the product/service",
        type: "textarea",
        required: true,
        placeholderAr: "اكتب وصفًا فنيًا وتطبيقيًا للمنتج أو الخدمة",
        placeholderEn: "Write a technical and practical description",
        noteAr: "يرجى مراعاة التفاصيل قدر الإمكان.",
        noteEn: "Please include as much detail as possible.",
      },
      {
        id: "project_nature",
        labelAr: "2. هل يعتمد المشروع على إنتاج مادي، تقديم خدمات، أو كلاهما؟",
        labelEn: "2. Does the project depend on physical production, services, or both?",
        type: "radio",
        required: true,
        options: [
          ["physical", "إنتاج مادي (مصنع، ورشة، خط إنتاج)", "Physical production"],
          ["service", "خدمة (مراكز، تطبيقات، أعمال مهنية)", "Service"],
          ["both", "كلاهما", "Both"],
        ],
      },
    ],
  },
  {
    ar: "ثانيًا: تحديد الطاقة الإنتاجية / القدرة التشغيلية",
    en: "2) Production Capacity / Operating Capability",
    questions: [
      {
        id: "production_unit",
        labelAr: "3. ما هي وحدة قياس الإنتاج المناسبة لمشروعك؟",
        labelEn: "3. What production measurement unit fits your project?",
        type: "text",
        required: true,
        placeholderAr: "كيلوغرام، متر، قطعة، ساعة عمل، عميل، طلب، أخرى",
        placeholderEn: "Kilogram, meter, unit, work hour, client, order, other",
      },
      {
        id: "monthly_capacity",
        labelAr: "4. الطاقة الإنتاجية المستهدفة شهريًا",
        labelEn: "4. Target monthly production capacity",
        type: "number",
        required: true,
      },
      {
        id: "max_capacity",
        labelAr: "5. الطاقة القصوى الممكنة عند التوسع",
        labelEn: "5. Maximum possible capacity after expansion",
        type: "number",
        required: true,
      },
      {
        id: "daily_shifts",
        labelAr: "6. عدد ورديات التشغيل اليومية المتوقعة",
        labelEn: "6. Expected daily operating shifts",
        type: "radio",
        required: true,
        options: [
          ["one", "وردية واحدة", "One shift"],
          ["two", "ورديتان", "Two shifts"],
          ["three", "3 ورديات", "Three shifts"],
          ["seasonal", "حسب المواسم", "Seasonal"],
        ],
        detailsId: "daily_shifts_details",
        detailsAr: "اشرح في حال كان التشغيل حسب المواسم",
        detailsEn: "Explain if operation is seasonal",
      },
      {
        id: "shift_hours",
        labelAr: "7. مدة الوردية الواحدة (بالساعات)",
        labelEn: "7. Duration of one shift in hours",
        type: "number",
        required: true,
      },
      {
        id: "trial_minimum_output",
        labelAr: "8. الحد الأدنى لإنتاج التشغيل التجريبي إن وجد",
        labelEn: "8. Minimum trial operation output, if any",
        type: "number",
      },
    ],
  },
  {
    ar: "ثالثًا: الموقع ومتطلبات البنية التحتية",
    en: "3) Location and Infrastructure Requirements",
    questions: [
      {
        id: "site_type",
        labelAr: "9. نوع الموقع المطلوب للتشغيل",
        labelEn: "9. Required operating site type",
        type: "checkbox",
        required: true,
        options: [
          ["factory", "مصنع", "Factory"],
          ["warehouse", "مستودع", "Warehouse"],
          ["office", "مكتب إداري", "Administrative office"],
          ["shop", "محل تجاري", "Retail shop"],
          ["field", "مساحة مفتوحة/موقع ميداني", "Open/field site"],
          ["cloud", "موقع إلكتروني/سحابي (للمشاريع الرقمية)", "Online/cloud site"],
          ["other", "أخرى", "Other"],
        ],
        detailsId: "site_type_other",
        detailsAr: "أخرى",
        detailsEn: "Other",
      },
      {
        id: "required_area",
        labelAr: "10. المساحة المطلوبة (متر مربع)",
        labelEn: "10. Required area in square meters",
        type: "number",
        required: true,
      },
      {
        id: "preferred_location",
        labelAr: "11. الموقع الجغرافي المفضل",
        labelEn: "11. Preferred geographic location",
        type: "text",
        required: true,
      },
      {
        id: "electricity_requirements",
        labelAr: "12. متطلبات الكهرباء",
        labelEn: "12. Electricity requirements",
        type: "checkbox",
        required: true,
        options: [
          ["110", "جهد 110", "110 voltage"],
          ["220", "جهد 220", "220 voltage"],
          ["380", "جهد 380", "380 voltage"],
          ["high", "قدرة عالية", "High capacity"],
          ["none", "لا يتطلب", "Not required"],
          ["other", "أخرى", "Other"],
        ],
        detailsId: "electricity_high_details",
        detailsAr: "حدد القدرة العالية أو المتطلب الآخر",
        detailsEn: "Specify high capacity or other requirement",
      },
      {
        id: "water_requirements",
        labelAr: "13. متطلبات المياه",
        labelEn: "13. Water requirements",
        type: "radio",
        required: true,
        options: [
          ["yes", "نعم", "Yes"],
          ["no", "لا", "No"],
        ],
        detailsId: "water_daily_quantity",
        detailsAr: "كمية تقديرية يومية إن وجد",
        detailsEn: "Estimated daily quantity, if any",
      },
      {
        id: "network_requirements",
        labelAr: "14. متطلبات شبكات الاتصالات والإنترنت",
        labelEn: "14. Communication and internet requirements",
        type: "checkbox",
        required: true,
        options: [
          ["fast_internet", "اتصال إنترنت عالي السرعة", "High-speed internet"],
          ["lan", "شبكة داخلية LAN", "Internal LAN"],
          ["servers", "سيرفرات داخلية", "Internal servers"],
          ["cloud", "حلول سحابية فقط", "Cloud-only solutions"],
          ["none", "لا يوجد احتياج", "No requirement"],
        ],
      },
      {
        id: "safety_requirements",
        labelAr: "15. متطلبات السلامة",
        labelEn: "15. Safety requirements",
        type: "checkbox",
        required: true,
        options: [
          ["fire", "نظام مكافحة حرائق", "Firefighting system"],
          ["exits", "مخارج طوارئ", "Emergency exits"],
          ["ventilation", "تهوية صناعية", "Industrial ventilation"],
          ["ppe", "معدات سلامة فردية", "Personal protective equipment"],
          ["cameras", "كاميرات مراقبة", "Surveillance cameras"],
          ["other", "أخرى", "Other"],
        ],
        detailsId: "safety_other",
        detailsAr: "أخرى",
        detailsEn: "Other",
      },
      {
        id: "permits_required",
        labelAr: "16. هل يتطلب المشروع تصاريح بلدية أو صناعية معينة؟",
        labelEn: "16. Does the project require municipal or industrial permits?",
        type: "radio",
        required: true,
        options: [
          ["yes", "نعم", "Yes"],
          ["no", "لا", "No"],
        ],
        detailsId: "permits_details",
        detailsAr: "حدد التصاريح المطلوبة",
        detailsEn: "Specify required permits",
      },
    ],
  },
  {
    ar: "رابعًا: احتياجات المعدات والتقنيات",
    en: "4) Equipment and Technology Needs",
    questions: [
      {
        id: "main_equipment",
        labelAr: "17. قائمة المعدات الأساسية المطلوبة",
        labelEn: "17. List of main required equipment",
        type: "textarea",
        required: true,
        placeholderAr: "يمكن إدخال المعدات مفصولة بفواصل",
        placeholderEn: "You can enter equipment separated by commas",
      },
      {
        id: "support_equipment",
        labelAr: "18. هل هناك معدات مساندة إضافية؟",
        labelEn: "18. Are there additional supporting equipment needs?",
        type: "radio",
        required: true,
        options: [
          ["yes", "نعم", "Yes"],
          ["no", "لا", "No"],
        ],
        detailsId: "support_equipment_details",
        detailsAr: "حدد المعدات المساندة",
        detailsEn: "Specify supporting equipment",
      },
      {
        id: "technology_type",
        labelAr: "19. نوع التقنية المطلوبة للتشغيل",
        labelEn: "19. Required technology type for operation",
        type: "checkbox",
        required: true,
        options: [
          ["computers", "أجهزة حاسوب", "Computers"],
          ["systems", "أنظمة إدارة (ERP – POS – CRM)", "Management systems"],
          ["ai", "أنظمة ذكاء اصطناعي", "AI systems"],
          ["analytics", "أدوات تحليل بيانات", "Data analysis tools"],
          ["accounting", "أنظمة محاسبية", "Accounting systems"],
          ["custom_software", "برمجيات خاصة", "Custom software"],
          ["other", "أخرى", "Other"],
        ],
        detailsId: "technology_other",
        detailsAr: "أخرى",
        detailsEn: "Other",
      },
      {
        id: "custom_system_needed",
        labelAr: "20. هل يحتاج المشروع إلى برمجة نظام خاص؟",
        labelEn: "20. Does the project require custom system development?",
        type: "radio",
        required: true,
        options: [
          ["yes", "نعم", "Yes"],
          ["no", "لا", "No"],
          ["unsure", "غير متأكد (نحدد ذلك وفق الدراسة)", "Not sure"],
        ],
      },
      {
        id: "maintenance_needed",
        labelAr: "21. هل المعدات تحتاج صيانة دورية؟",
        labelEn: "21. Does the equipment require periodic maintenance?",
        type: "radio",
        required: true,
        options: [
          ["yes", "نعم", "Yes"],
          ["no", "لا", "No"],
        ],
        detailsId: "maintenance_cycle",
        detailsAr: "حدد دورية الصيانة",
        detailsEn: "Specify maintenance frequency",
      },
      {
        id: "equipment_life_years",
        labelAr: "22. مدة العمر الافتراضي للمعدات الأساسية إن وجد (بالسنوات)",
        labelEn: "22. Expected useful life of main equipment in years, if any",
        type: "number",
      },
    ],
  },
  {
    ar: "خامسًا: آليات التشغيل والإنتاج",
    en: "5) Operation and Production Mechanisms",
    questions: [
      {
        id: "process_flow",
        labelAr: "23. وصف خطوات الإنتاج أو تقديم الخدمة من البداية إلى النهاية",
        labelEn: "23. Describe the production/service process flow end to end",
        type: "textarea",
        required: true,
        noteAr: "يرجى مراعاة التفاصيل في وصف الخطوات من البداية إلى النهاية.",
        noteEn: "Please include detailed steps from start to finish.",
      },
      {
        id: "process_stages_count",
        labelAr: "24. عدد مراحل الإنتاج أو الخدمة",
        labelEn: "24. Number of production/service stages",
        type: "number",
        required: true,
      },
      {
        id: "stage_timing",
        labelAr: "25. تحديد زمن تنفيذ كل مرحلة (بالدقائق أو الساعات)",
        labelEn: "25. Execution time for each stage in minutes or hours",
        type: "textarea",
        required: true,
        placeholderAr: "اكتب الزمن لكل مرحلة بشكل منظم ومفصل",
        placeholderEn: "Write the time for each stage in a structured and detailed way",
      },
      {
        id: "external_parties",
        labelAr: "26. هل تتضمن العملية التشغيلية اعتمادًا على أطراف خارجية؟",
        labelEn: "26. Does operation depend on external parties?",
        type: "checkbox",
        required: true,
        options: [
          ["suppliers", "موردين", "Suppliers"],
          ["transport", "شركات نقل", "Transport companies"],
          ["technicians", "فنيين", "Technicians"],
          ["tech_companies", "شركات تقنية", "Technology companies"],
          ["other", "أخرى", "Other"],
        ],
        detailsId: "external_parties_other",
        detailsAr: "أخرى",
        detailsEn: "Other",
      },
      {
        id: "quality_standards",
        labelAr: "27. ما هي معايير الجودة المتبعة في التشغيل؟",
        labelEn: "27. What quality standards are followed in operation?",
        type: "textarea",
        required: true,
        placeholderAr: "ISO، معايير داخلية، مواصفات فنية...",
        placeholderEn: "ISO, internal standards, technical specifications...",
      },
      {
        id: "storage_requirements",
        labelAr: "28. متطلبات التخزين إن وجد",
        labelEn: "28. Storage requirements, if any",
        type: "checkbox",
        required: true,
        options: [
          ["normal", "تخزين عادي", "Normal storage"],
          ["cooling", "تبريد", "Cooling"],
          ["freezing", "تجميد", "Freezing"],
          ["flammable", "مواد قابلة للاشتعال", "Flammable materials"],
          ["none", "لا توجد احتياجات", "No needs"],
        ],
      },
    ],
  },
  {
    ar: "سادسًا: تصميم العمليات التشغيلية",
    en: "6) Operations Design",
    questions: [
   {
  id: "layout_plan",
  labelAr: "29. هل هناك مخطط مقترح لتوزيع المعدات داخل الموقع؟",
  labelEn: "29. Is there a proposed equipment layout plan?",
  type: "radio",
  required: true,
  options: [
    ["yes", "نعم", "Yes"],
    ["no", "لا", "No"],
  ],
  fileOnYes: {
    id: "layout_file",
    labelAr: "إرفاق ملف المخطط",
    labelEn: "Attach layout file",
    multiple: false,
  },
},
      {
        id: "employees_count",
        labelAr: "30. عدد الموظفين المطلوب تشغيلهم",
        labelEn: "30. Required number of employees",
        type: "textarea",
        required: true,
        placeholderAr: "فنيين، إداريين، تشغيليين، سائقين، أخرى",
        placeholderEn: "Technicians, admins, operators, drivers, other",
      },
      {
        id: "job_descriptions",
        labelAr: "31. توصيف المهام لكل وظيفة تشغيلية",
        labelEn: "31. Task descriptions for each operational role",
        type: "textarea",
        required: true,
      },
      {
        id: "training_requirements",
        labelAr: "32. متطلبات التدريب للموظفين",
        labelEn: "32. Employee training requirements",
        type: "checkbox",
        required: true,
        options: [
          ["equipment", "تدريب على المعدات", "Equipment training"],
          ["operational", "تدريب تشغيلي", "Operational training"],
          ["technical", "تدريب تقني", "Technical training"],
          ["none", "لا يوجد", "None"],
        ],
      },
      {
        id: "operational_safety",
        labelAr: "33. إجراءات السلامة التشغيلية",
        labelEn: "33. Operational safety procedures",
        type: "textarea",
        required: true,
      },
      {
        id: "waste_rate",
        labelAr: "34. تقدير معدل الهدر المتوقع شهريًا إن وجد",
        labelEn: "34. Estimated monthly waste rate, if any",
        type: "radio",
        required: true,
        options: [
          ["low", "منخفض (0-5%)", "Low (0-5%)"],
          ["medium", "متوسط (5-15%)", "Medium (5-15%)"],
          ["high", "مرتفع (15-30%)", "High (15-30%)"],
          ["none", "لا يوجد هدر", "No waste"],
        ],
      },
      {
        id: "automation_level",
        labelAr: "35. مستوى الأتمتة المطلوبة في التشغيل",
        labelEn: "35. Required operation automation level",
        type: "radio",
        required: true,
        options: [
          ["manual", "يدوي بالكامل", "Fully manual"],
          ["semi", "نصف آلي", "Semi-automated"],
          ["automated", "آلي", "Automated"],
          ["ai", "آلي مع ذكاء اصطناعي", "Automated with AI"],
        ],
      },
    ],
  },
  {
    ar: "سابعًا: معلومات إضافية",
    en: "7) Additional Information",
    questions: [
     {
  id: "extra_files_choice",
  labelAr: "36. هل ترغب بإرفاق أي رسومات أو مخططات أو قوائم معدات؟",
  labelEn: "36. Would you like to attach drawings, layouts, or equipment lists?",
  type: "radio",
  options: [
    ["yes", "نعم", "Yes"],
    ["no", "لا", "No"],
  ],
  fileOnYes: {
    id: "extra_files",
    labelAr: "رفع الملفات",
    labelEn: "Upload files",
    multiple: true,
  },
},
      {
        id: "additional_notes",
        labelAr: "37. أي ملاحظات أخرى مهمة للدراسة الفنية؟",
        labelEn: "37. Any other notes important for the technical study?",
        type: "textarea",
      },
    ],
  },
];

const GENERATE_ENDPOINT =
  process.env.REACT_APP_GENERATE_ENDPOINT ||
  "https://api.al-jabriyah.com/generate";

const GENERATION_STATUS_KEY = "technical-survey-generation-status";

function TechnicalSurvey({ language = "ar", setLanguage = () => {} }) {
  const [surveyContext, setSurveyContext] = useState({
    studyId: "",
    requestRef: "",
    storageKey: "",
  });
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationNow, setGenerationNow] = useState(Date.now());
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [detailSelections, setDetailSelections] = useState({});
  const [countryQuery, setCountryQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [countryPanelOpen, setCountryPanelOpen] = useState(false);
  const [cityPanelOpen, setCityPanelOpen] = useState(false);
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState({});
  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [selectedCityName, setSelectedCityName] = useState("");
  const abortControllerRef = useRef(null);
  const cancelGenerationRef = useRef(false);
  const isArabic = language === "ar";
  const isGenerating = Boolean(generationStatus);

  const requiredGroups = useMemo(() => {
    return sections.flatMap((section) =>
      section.questions
        .filter((question) => question.required && ["radio", "checkbox"].includes(question.type))
        .map((question) => question.id)
    );
  }, []);

  const questionNumbers = useMemo(() => {
    const numbers = {};
    let index = 1;

    sections.forEach((section) => {
      section.questions.forEach((question) => {
        numbers[question.id] = index;
        index += 1;
      });
    });

    return numbers;
  }, []);

  const countries = useMemo(() => Country.getAllCountries(), []);
const countryNamesAr = useMemo(
  () => new Intl.DisplayNames(["ar"], { type: "region" }),
  [],
);

const countryNamesEn = useMemo(
  () => new Intl.DisplayNames(["en"], { type: "region" }),
  [],
);
  const filteredCountries = useMemo(() => {
  const query = countryQuery.trim().toLowerCase();

  if (!query) return countries;

  return countries.filter((country) => {
    const arabicName = countryNamesAr.of(country.isoCode) || "";
    const englishName = countryNamesEn.of(country.isoCode) || country.name;

    return (
      country.name.toLowerCase().includes(query) ||
      englishName.toLowerCase().includes(query) ||
      arabicName.toLowerCase().includes(query) ||
      country.isoCode.toLowerCase().includes(query)
    );
  });
}, [
  countries,
  countryQuery,
  countryNamesAr,
  countryNamesEn,
]);

  const cities = useMemo(() => {
    if (!selectedCountryIso) return [];
    return City.getCitiesOfCountry(selectedCountryIso) || [];
  }, [selectedCountryIso]);

const filteredCities = useMemo(() => {
  const query = String(cityQuery || "")
    .trim()
    .toLowerCase();

  console.log("🟢 CITY QUERY:", query);
  console.log("🟢 ALL CITIES:", cities.length);

  if (!query) return cities;

  const result = cities.filter((city) =>
    String(city?.name || "")
      .toLowerCase()
      .includes(query),
  );

  console.log("🟢 FILTERED CITIES:", result.length);
  console.log(
    "🟢 FILTERED NAMES:",
    result.slice(0, 10).map((city) => city.name),
  );

  return result;
}, [cities, cityQuery]);

  function t(ar, en) {
    return isArabic ? ar : en || ar;
  }

  function stripQuestionNumber(text) {
    return String(text || "").replace(/^\s*[\d٠-٩]+\s*[.)-]?\s*/, "");
  }

  function getQuestionLabel(question) {
    const label = stripQuestionNumber(t(question.labelAr, question.labelEn));
    return `${questionNumbers[question.id]}. ${label}`;
  }

  function clearErrors(form) {
    form.querySelectorAll(".field-error").forEach((field) => {
      field.classList.remove("field-error");
    });

    form.querySelectorAll(".field-error-msg").forEach((message) => {
      message.remove();
    });
  }

  function markError(field, message) {
    if (!field) return;

    field.classList.add("field-error");
    const holder = field.closest(".field") || field.parentElement;
    if (!holder) return;

    const messageEl = document.createElement("div");
    messageEl.className = "field-error-msg";
    messageEl.textContent = message;
    holder.appendChild(messageEl);
  }

  function getDetailTrigger(question) {
    if (question.options?.some(([value]) => value === "other")) return "other";

    const triggersByQuestion = {
      daily_shifts: "seasonal",
      electricity_requirements: "high",
      water_requirements: "yes",
      permits_required: "yes",
      support_equipment: "yes",
      maintenance_needed: "yes",
    };

    return triggersByQuestion[question.id] || "yes";
  }

  function getQuestionSelection(form, question) {
    const inputs = Array.from(form.querySelectorAll(`input[name="${question.id}"]`));

    if (question.type === "checkbox") {
      return inputs.filter((input) => input.checked).map((input) => input.value);
    }

    return inputs.find((input) => input.checked)?.value || "";
  }

  function updateDetailSelectionsFromForm(form) {
    const nextSelections = {};

    sections.forEach((section) => {
      section.questions.forEach((question) => {
        if (
  (question.detailsId || question.fileOnYes) &&
  ["radio", "checkbox"].includes(question.type)
) {
          nextSelections[question.id] = getQuestionSelection(form, question);
        }
      });
    });

    setDetailSelections(nextSelections);
  }

  function handleChoiceChange(question) {
    const form = document.querySelector("#surveyForm");
    if (!form) return;

    setDetailSelections((current) => ({
      ...current,
      [question.id]: getQuestionSelection(form, question),
    }));
  }

  function isDetailVisible(question) {
    if (!question.detailsId) return false;

    if (question.id === "electricity_requirements") {
      const selected = detailSelections[question.id];
      return Array.isArray(selected)
        ? selected.includes("high") || selected.includes("other")
        : selected === "high" || selected === "other";
    }

    const trigger = getDetailTrigger(question);
    const selected = detailSelections[question.id];

    return Array.isArray(selected)
      ? selected.includes(trigger)
      : selected === trigger;
  }

  function getSurveyParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      studyId: params.get("study_id") || "",
      requestRef: params.get("request_ref") || "",
    };
  }

  function getStorageKey(ctx = surveyContext) {
    return ctx.storageKey || "";
  }

  function getStudyResultKey(ctx = surveyContext) {
    const keyPart = ctx.studyId || ctx.requestRef || "";
    return keyPart ? `technical-study-result:${keyPart}` : "technical_study_result";
  }

  function getSavedStudyResult(ctx = surveyContext) {
    const scopedKey = getStudyResultKey(ctx);
    return (
      (scopedKey !== "technical_study_result" ? localStorage.getItem(scopedKey) : null) ||
      localStorage.getItem("technical_study_result") ||
      localStorage.getItem("study_result")
    );
  }

  function saveStudyResult(study, ctx = surveyContext) {
    const serialized = JSON.stringify(study);
    localStorage.setItem("technical_study_result", serialized);
    localStorage.setItem("study_result", serialized);

    const scopedKey = getStudyResultKey(ctx);
    if (scopedKey !== "technical_study_result") {
      localStorage.setItem(scopedKey, serialized);
    }
  }

  function getProgressMeta(formDataLike) {
    const form = document.querySelector("#surveyForm");
    if (!form) return { step: 1, progress: 0 };

    const allNamedFields = Array.from(
      form.querySelectorAll("input[name], select[name], textarea[name]")
    ).filter((field) => field.name && !field.disabled && field.type !== "hidden" && field.type !== "file");

    const uniqueNames = Array.from(new Set(allNamedFields.map((field) => field.name)));
    const answers = formDataLike || new FormData(form);
    const completedCount = uniqueNames.filter((name) => {
      const values = answers.getAll(name).filter((value) => String(value || "").trim() !== "");
      return values.length > 0;
    }).length;

    const progress = uniqueNames.length
      ? Math.min(100, Math.round((completedCount / uniqueNames.length) * 100))
      : 0;

    return { step: progress >= 100 ? 2 : 1, progress };
  }

  function readFormValues(form) {
    const values = {};

    Array.from(form.elements).forEach((field) => {
      if (!field.name || field.type === "file" || field.disabled) return;

      if (field.type === "checkbox") {
        if (!values[field.name]) values[field.name] = [];
        if (field.checked) values[field.name].push(field.value);
        return;
      }

      if (field.type === "radio") {
        if (field.checked) values[field.name] = field.value;
        return;
      }

      values[field.name] = field.value;
    });

    return values;
  }

  function saveDraftLocally(ctx = surveyContext) {
    const storageKey = getStorageKey(ctx);
    const form = document.querySelector("#surveyForm");
    if (!storageKey || !form) return;

    const fd = new FormData(form);

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        values: readFormValues(form),
        meta: getProgressMeta(fd),
        savedAt: new Date().toISOString(),
      })
    );
  }

  function restoreDraftLocally(ctx = surveyContext) {
    const storageKey = getStorageKey(ctx);
    const form = document.querySelector("#surveyForm");
    if (!storageKey || !form) return;

    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      const values = parsed?.values || {};

      Array.from(form.querySelectorAll("input[name], select[name], textarea[name]")).forEach((field) => {
        if (!field.name || field.type === "file") return;

        const value = values[field.name];
        if (value === undefined) return;

        if (field.type === "checkbox") {
          field.checked = (Array.isArray(value) ? value : [value]).includes(field.value);
          return;
        }

        if (field.type === "radio") {
          field.checked = value === field.value;
          return;
        }

        field.value = value;
      });

      if (["ar", "en"].includes(values.report_language)) {
        setLanguage(values.report_language);
      }

      if (values.target_country) {
        const restoredCountry = Country.getAllCountries().find(
          (country) => country.name === values.target_country
        );

        setSelectedCountryName(values.target_country);
        setSelectedCountryIso(restoredCountry?.isoCode || "");
      }

      if (values.target_city) {
        setSelectedCityName(values.target_city);
      }

      updateDetailSelectionsFromForm(form);
    } catch (error) {
      console.error("Failed to restore technical survey draft:", error);
    }
  }

  async function syncSurveyProgress(ctx = surveyContext, override = {}) {
    if (!ctx.studyId) return;

    const form = document.querySelector("#surveyForm");
    if (!form) return;

    const fd = new FormData(form);
    const meta = getProgressMeta(fd);
    const isStarted = meta.progress > 0;

    const { error } = await supabase
      .from("studies")
      .update({
        survey_current_step: override.survey_current_step || meta.step,
        survey_progress: override.survey_progress ?? meta.progress,
        status: override.status || (isStarted ? "data_collection" : "waiting_for_client"),
        last_update_note:
          override.last_update_note ||
          (isStarted
            ? "Client is filling in the technical and operational survey."
            : "Waiting for the client to start the technical and operational survey."),
        last_updated_at: new Date().toISOString(),
      })
      .eq("id", ctx.studyId);

    if (error) {
      console.error("Failed to sync technical survey progress:", error);
    }
  }

  async function resolveSurveyContext() {
    const params = getSurveyParams();
    let { studyId, requestRef } = params;

    if (!studyId && requestRef) {
      const { data: orderData } = await supabase
        .from("orders")
        .select("id")
        .eq("request_ref", requestRef)
        .maybeSingle();

      if (orderData?.id) {
        const { data: studyData } = await supabase
          .from("studies")
          .select("id")
          .eq("order_id", orderData.id)
          .maybeSingle();

        studyId = studyData?.id || "";
      }
    }

    if (studyId && !requestRef) {
      const { data: studyData } = await supabase
        .from("studies")
        .select("order_id")
        .eq("id", studyId)
        .maybeSingle();

      if (studyData?.order_id) {
        const { data: orderData } = await supabase
          .from("orders")
          .select("request_ref")
          .eq("id", studyData.order_id)
          .maybeSingle();

        requestRef = orderData?.request_ref || "";
      }
    }

    setSurveyContext({
      studyId,
      requestRef,
      storageKey: `technical-survey-draft:${studyId || requestRef || "anonymous"}`,
    });
  }

  function startGenerationStatus(payload) {
    const nextStatus = {
      startedAt: Date.now(),
      requestId: payload?.request_id || surveyContext.requestRef || "",
      projectName: payload?.project_name || payload?.recipient_name || "",
      endpoint: GENERATE_ENDPOINT,
    };

    localStorage.setItem(GENERATION_STATUS_KEY, JSON.stringify(nextStatus));
    setGenerationStatus(nextStatus);
    setGenerationNow(Date.now());
  }

  function clearGenerationStatus() {
    localStorage.removeItem(GENERATION_STATUS_KEY);
    setGenerationStatus(null);
  }

  function formatGenerationTime(startedAt) {
    const elapsed = Math.max(0, Math.floor((generationNow - Number(startedAt || Date.now())) / 1000));
    const minutes = String(Math.floor(elapsed / 60)).padStart(2, "0");
    const seconds = String(elapsed % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  }

  function handleCancelGeneration() {
    cancelGenerationRef.current = true;
    abortControllerRef.current?.abort();
    clearGenerationStatus();
    setErrorMessage(t("تم إلغاء توليد الدراسة.", "Study generation was canceled."));
  }

  function collectFormData(form) {
    const fd = new FormData(form);
    const reportLang =
      form.querySelector('input[name="report_language"]:checked')?.value || language;

    fd.set("ui_lang", language);
    fd.set("language", reportLang);
    fd.set("report_language", reportLang);
    fd.set("study_type", "technical_operational");
    fd.set("study_name_raw", fd.get("project_name") || fd.get("recipient_name") || "");

    const done = new Set();
    Array.from(form.querySelectorAll("input[name], select[name], textarea[name]")).forEach((field) => {
      const name = field.name;
      if (!name || done.has(name) || name.startsWith("label__")) return;
      done.add(name);

      const fieldWrap = field.closest(".field");
      const labelText = String(fieldWrap?.querySelector("label")?.textContent || "")
        .replace(/\s+/g, " ")
        .trim();

      if (labelText) {
        fd.set(`label__${name}`, labelText);
      }
    });

    return fd;
  }

  function buildGeneratePayload(fd) {
    const payload = {};

    for (const [key, value] of fd.entries()) {
      if (key.startsWith("label__")) continue;

      if (value instanceof File) {
        if (!value.name) continue;
        payload[key] = payload[key]
          ? [].concat(payload[key], value.name)
          : value.name;
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        payload[key] = Array.isArray(payload[key])
          ? [...payload[key], value]
          : [payload[key], value];
      } else {
        payload[key] = value;
      }
    }

    payload.report_language = payload.report_language || language;
    payload.language = payload.report_language;
    payload.ui_lang = language;
    payload.study_type = "technical_operational";
    payload.study_title =
      language === "ar"
        ? "الدراسة الفنية والتشغيلية"
        : "Technical and Operational Study";

    return payload;
  }

  function validateForm(form) {
    const requiredMessage = t("هذا الحقل مطلوب.", "This field is required.");
    let isValid = true;

    setErrorMessage("");
    setSuccessMessage("");
    clearErrors(form);

    form.querySelectorAll("input[required], textarea[required]").forEach((field) => {
      if (!field.value.trim()) {
        isValid = false;
        markError(field, requiredMessage);
      }
    });

    requiredGroups.forEach((groupName) => {
      const inputs = Array.from(form.querySelectorAll(`input[name="${groupName}"]`));
      const hasSelection = inputs.some((input) => input.checked);

      if (!hasSelection) {
        isValid = false;
        markError(inputs[0], requiredMessage);
      }
    });

    if (!isValid) {
      setErrorMessage(t("يرجى تعبئة الحقول المطلوبة.", "Please fill in the required fields."));
      return false;
    }

    return true;
  }

  async function handleGenerateClick() {
    const form = document.querySelector("#surveyForm");
    if (!form) return;

    if (isGenerating) {
      setErrorMessage(
        t(
          "يوجد توليد دراسة جارٍ الآن. يمكنك الانتظار أو الضغط على إلغاء.",
          "A study is already being generated. Please wait or cancel it."
        )
      );
      return;
    }

    if (!validateForm(form)) return;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    cancelGenerationRef.current = false;
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const fd = collectFormData(form);
      fd.append("request_id", surveyContext.requestRef || "");
      const payload = buildGeneratePayload(fd);
console.log("🔵 TECHNICAL PAYLOAD CHECK:", payload);
console.log("🔵 STUDY TYPE:", payload.study_type);
console.log("🔵 REPORT LANGUAGE:", payload.report_language);
console.log("🔵 PROJECT NAME:", payload.project_name);
console.log("🔵 TARGET COUNTRY:", payload.target_country);
console.log("🔵 TARGET CITY:", payload.target_city);
console.log("🔵 TECHNICAL DESCRIPTION:", payload.technical_description);
console.log("🔵 MONTHLY CAPACITY:", payload.monthly_capacity);
console.log("🔵 WATER REQUIREMENTS:", payload.water_requirements);
console.log("🔵 MAIN EQUIPMENT:", payload.main_equipment);
console.log("🔵 PROCESS FLOW:", payload.process_flow);
console.log("🔵 EMPLOYEES COUNT:", payload.employees_count);
console.log("🔵 AUTOMATION LEVEL:", payload.automation_level); 
      saveDraftLocally();
      await syncSurveyProgress();
      startGenerationStatus(payload);

      const res = await fetch(GENERATE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "alj_9X#study_2026_apikey",
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!res.ok) {
        let errText = "";
        try {
          const json = await res.json();
          errText = json?.error || json?.message || JSON.stringify(json);
        } catch (_error) {}

        throw new Error(errText || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data?.success === false || data?.error) {
        throw new Error(data?.message || data?.error);
      }

      const studyWithMeta = {
        ...(data?.study || data || {}),
        ...payload,
        technical_survey_payload: payload,
        recipient_name: payload.recipient_name || "",
        project_name: payload.project_name || payload.recipient_name || "",
        project_desc: payload.project_desc || "",
        activity_category: payload.activity_category || "",
        target_country: payload.target_country || "",
        target_city: payload.target_city || "",
        report_language: payload.report_language || language,
        language: payload.language || language,
        ui_lang: language,
        study_type: "technical_operational",
      };

      saveStudyResult(studyWithMeta);
      await generateWord(studyWithMeta);

      await syncSurveyProgress(surveyContext, {
        survey_current_step: 2,
        survey_progress: 100,
        status: "analysis",
        last_update_note: "Technical and operational survey submitted successfully.",
      });

      setSuccessMessage(
        t(
          "تم توليد الدراسة الفنية والتشغيلية بنجاح.",
          "The technical and operational study has been generated successfully."
        )
      );
      clearGenerationStatus();
      form.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (error) {
      console.error("Technical Generate Study Error:", error);

      if (error?.name === "AbortError" || cancelGenerationRef.current) {
        setErrorMessage(t("تم إلغاء توليد الدراسة.", "Study generation was canceled."));
      } else {
        const displayMessage =
          error instanceof TypeError && error.message === "Failed to fetch"
            ? t(
                "تعذر الوصول إلى خادم التوليد. تأكد أن خدمة التوليد تعمل ثم حاول مرة أخرى.",
                "Could not reach the generation server. Make sure the generation service is running, then try again."
              )
            : error.message || t("حدث خطأ أثناء توليد الدراسة.", "An error occurred while generating the study.");

        setErrorMessage(displayMessage);
      }

      clearGenerationStatus();
    } finally {
      abortControllerRef.current = null;
      cancelGenerationRef.current = false;
    }
  }

  async function exportSavedWord() {
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const savedStudy = getSavedStudyResult();

      if (!savedStudy) {
        setErrorMessage(
          t(
            "لا توجد دراسة محفوظة. يجب توليد دراسة ناجحة مرة واحدة أولًا.",
            "No saved study found. Generate a successful study first."
          )
        );
        return;
      }

      const study = JSON.parse(savedStudy);

      await generateWord({
        ...study,
        report_language: study.report_language || language,
        language: study.language || language,
        ui_lang: study.ui_lang || language,
        study_type: study.study_type || "technical_operational",
      });

      setSuccessMessage(
        t(
          "تمت إعادة توليد ملف Word بنجاح.",
          "The Word file has been regenerated successfully."
        )
      );
    } catch (error) {
      console.error("Failed to regenerate technical Word file:", error);
      setErrorMessage(
        t(
          "حدث خطأ أثناء إعادة توليد ملف Word.",
          "An error occurred while regenerating the Word file."
        )
      );
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    handleGenerateClick();
  }

  function handleCountrySelect(country) {
    setSelectedCountryName(country.name);
    setSelectedCountryIso(country.isoCode);
    setSelectedCityName("");
    setCountryQuery("");
    setCityQuery("");
    setCountryPanelOpen(false);
    setCityPanelOpen(false);
  }

  function handleCitySelect(city) {
    setSelectedCityName(city.name);
    setCityQuery("");
    setCityPanelOpen(false);
  }

  function preventNegativeNumberKey(event) {
    if (["-", "+", "e", "E"].includes(event.key)) {
      event.preventDefault();
    }
  }

  function clampNumberToZero(event) {
    const field = event.currentTarget;
    if (field.value !== "" && Number(field.value) < 0) {
      field.value = "0";
    }
  }

  useEffect(() => {
    resolveSurveyContext();

    const rawStatus = localStorage.getItem(GENERATION_STATUS_KEY);
    if (!rawStatus) return;

    try {
      const parsed = JSON.parse(rawStatus);
      if (parsed?.startedAt) {
        setGenerationStatus(parsed);
        setGenerationNow(Date.now());
      }
    } catch (_error) {
      localStorage.removeItem(GENERATION_STATUS_KEY);
    }
  }, []);

  useEffect(() => {
    if (!generationStatus) return undefined;

    const timer = window.setInterval(() => {
      setGenerationNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [generationStatus]);

  useEffect(() => {
    if (!surveyContext.storageKey) return undefined;

    const form = document.querySelector("#surveyForm");
    if (!form) return undefined;

    restoreDraftLocally(surveyContext);

    const saveAndSync = () => {
      saveDraftLocally(surveyContext);
      syncSurveyProgress(surveyContext);
    };

    const handleInput = (event) => {
      if (event.target?.matches("input, select, textarea")) {
        window.clearTimeout(handleInput.timerId);
        handleInput.timerId = window.setTimeout(saveAndSync, 300);
      }
    };

    form.addEventListener("input", handleInput);
    form.addEventListener("change", handleInput);
    saveAndSync();

    return () => {
      form.removeEventListener("input", handleInput);
      form.removeEventListener("change", handleInput);
      window.clearTimeout(handleInput.timerId);
    };
  }, [surveyContext.storageKey]);

  useEffect(() => {
    if (!surveyContext.storageKey) return;

    const timer = window.setTimeout(() => {
      saveDraftLocally(surveyContext);
      syncSurveyProgress(surveyContext);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [selectedCountryName, selectedCityName]);
function renderInlineFileUpload(fileConfig) {
  const files = selectedFiles[fileConfig.id] || [];

  const syncInputFiles = (input, nextFiles) => {
    const dataTransfer = new DataTransfer();

    nextFiles.forEach((file) => {
      dataTransfer.items.add(file);
    });

    input.files = dataTransfer.files;
  };

  return (
    <div className="field col-12">
      <label htmlFor={fileConfig.id}>
        <span>{t(fileConfig.labelAr, fileConfig.labelEn)}</span>
      </label>

      <input
        id={fileConfig.id}
        name={fileConfig.id}
        type="file"
        multiple={Boolean(fileConfig.multiple)}
        onChange={(event) => {
          const input = event.currentTarget;
          const newFiles = Array.from(input.files || []);

          setSelectedFiles((current) => {
            const existingFiles = current[fileConfig.id] || [];

            let nextFiles;

            if (fileConfig.multiple) {
              nextFiles = [...existingFiles, ...newFiles].filter(
                (file, index, array) =>
                  index ===
                  array.findIndex(
                    (item) =>
                      item.name === file.name &&
                      item.size === file.size &&
                      item.lastModified === file.lastModified
                  )
              );
            } else {
              nextFiles = newFiles.slice(0, 1);
            }

            syncInputFiles(input, nextFiles);

            return {
              ...current,
              [fileConfig.id]: nextFiles,
            };
          });
        }}
      />

      {files.length > 0 && (
        <div className="uploaded-files-list">
          {files.map((file, index) => (
            <div
              className="uploaded-file-item"
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
            >
              <span>{file.name}</span>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const input = document.getElementById(fileConfig.id);

                  setSelectedFiles((current) => {
                    const nextFiles = (
                      current[fileConfig.id] || []
                    ).filter((_, fileIndex) => fileIndex !== index);

                    if (input) {
                      syncInputFiles(input, nextFiles);
                    }

                    return {
                      ...current,
                      [fileConfig.id]: nextFiles,
                    };
                  });
                }}
              >
                {t("إزالة", "Remove")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
  function renderQuestion(question) {
    const label = getQuestionLabel(question);
    const placeholder = t(question.placeholderAr || "", question.placeholderEn || "");

    if (question.id === "target_country") {
      return (
        <div className="field col-6" key={question.id}>
          <label htmlFor={question.id}>
            <span>{label}</span>
          </label>
          <div className="ss">
            <input
              id={question.id}
              name={question.id}
              type="text"
              className="ss-value"
              required={question.required}
              readOnly
              value={selectedCountryName}
              placeholder={placeholder}
              onClick={() => {
                setCountryPanelOpen((open) => !open);
                setCityPanelOpen(false);
              }}
            />
            {countryPanelOpen && (
              <div className="ss-panel">
                <input
                  type="text"
                  className="ss-search"
                  value={countryQuery}
                  onChange={(event) => setCountryQuery(event.target.value)}
                  placeholder={t("ابحث عن الدولة...", "Search country...")}
                  autoComplete="off"
                />
                <div className="ss-options" role="listbox" aria-label="country options">
                  {filteredCountries.length ? (
                    filteredCountries.map((country) => (
                      <button
                        type="button"
                        className="ss-option"
                        key={country.isoCode}
                        onClick={() => handleCountrySelect(country)}
                      >
                        {country.name}
                      </button>
                    ))
                  ) : (
                    <div className="ss-empty">{t("لا توجد نتائج", "No results")}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (question.id === "target_city") {
      const cityPlaceholder = selectedCountryIso
        ? placeholder
        : t("اختر الدولة أولًا", "Select country first");

      return (
        <div className="field col-6" key={question.id}>
          <label htmlFor={question.id}>
            <span>{label}</span>
          </label>
          <div className="ss">
            <input
              id={question.id}
              name={question.id}
              type="text"
              className="ss-value"
              readOnly
              disabled={!selectedCountryIso}
              value={selectedCityName}
              placeholder={cityPlaceholder}
              onClick={() => {
                if (!selectedCountryIso) return;
                setCityPanelOpen((open) => !open);
                setCountryPanelOpen(false);
              }}
            />
            {cityPanelOpen && selectedCountryIso && (
              <div className="ss-panel">
               <input
  type="text"
  className="ss-search"
  value={cityQuery}
  onChange={(event) => {
    console.log("🔴 CITY SEARCH:", event.target.value);
    setCityQuery(event.target.value);
  }}
  placeholder={t("ابحث عن المدينة...", "Search city...")}
  autoComplete="off"
/>
                <div className="ss-options" role="listbox" aria-label="city options">
                  {filteredCities.length ? (
                   filteredCities.map((city, index) => (
                      <button
                        type="button"
                        className="ss-option"
                        key={`${city.name}-${city.stateCode || ""}-${city.latitude}-${city.longitude}-${index}`}
                        onClick={() => handleCitySelect(city)}
                      >
                        {city.name}
                      </button>
                    ))
                  ) : (
                    <div className="ss-empty">{t("لا توجد نتائج", "No results")}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (question.type === "radio" || question.type === "checkbox") {
  const detailsVisible = isDetailVisible(question);

  const selectedValue = detailSelections[question.id];

  const fileVisible = question.fileOnYes
    ? Array.isArray(selectedValue)
      ? selectedValue.includes("yes")
      : selectedValue === "yes"
    : false;

      return (
        <div className="field col-12" key={question.id}>
          <label>
            <span>{label}</span>
          </label>
          {question.noteAr && (
            <p className="hint">{t(question.noteAr, question.noteEn)}</p>
          )}
          <div className="options">
            {question.options.map(([value, ar, en]) => (
              <label className="option" key={`${question.id}-${value}`}>
                <input
                  type={question.type}
                  name={question.id}
                  value={value}
                  required={question.type === "radio" && question.required}
                  data-required-group={question.type === "checkbox" && question.required ? question.id : undefined}
                  checked={question.id === "report_language" ? language === value : undefined}
                  onChange={() => {
                    if (question.id === "report_language") setLanguage(value);
                    handleChoiceChange(question);
                  }}
                />
                <span>{t(ar, en)}</span>
              </label>
            ))}
          </div>
          {question.detailsId && (
            <div className="field" style={{ display: detailsVisible ? "" : "none" }}>
              <input
                type="text"
                id={question.detailsId}
                name={question.detailsId}
                placeholder={t(question.detailsAr, question.detailsEn)}
                disabled={!detailsVisible}
              />
            </div>
          )}
          {question.fileOnYes && fileVisible && (
  renderInlineFileUpload(question.fileOnYes)
)}
        </div>
      );
    }

    if (question.type === "textarea") {
      return (
        <div className="field col-12" key={question.id}>
          <label htmlFor={question.id}>
            <span>{label}</span>
          </label>
          {question.noteAr && (
            <p className="hint">{t(question.noteAr, question.noteEn)}</p>
          )}
          <textarea
            id={question.id}
            name={question.id}
            required={question.required}
            placeholder={placeholder}
          />
        </div>
      );
    }

    if (question.type === "file") {
  const files = selectedFiles[question.id] || [];

  const syncInputFiles = (input, nextFiles) => {
    const dataTransfer = new DataTransfer();

    nextFiles.forEach((file) => {
      dataTransfer.items.add(file);
    });

    input.files = dataTransfer.files;
  };

  return (
    <div className="field col-12" key={question.id}>
      <label htmlFor={question.id}>
        <span>{label}</span>
      </label>

      <input
        id={question.id}
        name={question.id}
        type="file"
        multiple
        onChange={(event) => {
          const input = event.currentTarget;
          const newFiles = Array.from(input.files || []);

          setSelectedFiles((current) => {
            const existingFiles = current[question.id] || [];

            const mergedFiles = [...existingFiles, ...newFiles].filter(
              (file, index, array) =>
                index ===
                array.findIndex(
                  (item) =>
                    item.name === file.name &&
                    item.size === file.size &&
                    item.lastModified === file.lastModified,
                ),
            );

            syncInputFiles(input, mergedFiles);

            return {
              ...current,
              [question.id]: mergedFiles,
            };
          });
        }}
      />

      {files.length > 0 && (
        <div className="uploaded-files-list">
          {files.map((file, index) => (
            <div
              className="uploaded-file-item"
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
            >
              <span>{file.name}</span>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const input = document.getElementById(question.id);

                  setSelectedFiles((current) => {
                    const nextFiles = (current[question.id] || []).filter(
                      (_, fileIndex) => fileIndex !== index,
                    );

                    if (input) {
                      syncInputFiles(input, nextFiles);
                    }

                    return {
                      ...current,
                      [question.id]: nextFiles,
                    };
                  });
                }}
              >
                {t("إزالة", "Remove")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

    return (
      <div className={question.type === "number" ? "field col-6" : "field col-12"} key={question.id}>
        <label htmlFor={question.id}>
          <span>{label}</span>
        </label>
        {question.noteAr && (
          <p className="hint">{t(question.noteAr, question.noteEn)}</p>
        )}
        <input
          id={question.id}
          name={question.id}
          type={question.type}
          required={question.required}
          placeholder={placeholder}
          min={question.type === "number" ? 0 : undefined}
          inputMode={question.type === "number" ? "decimal" : undefined}
          onKeyDown={question.type === "number" ? preventNegativeNumberKey : undefined}
          onInput={question.type === "number" ? clampNumberToZero : undefined}
          onWheel={question.type === "number" ? (event) => event.currentTarget.blur() : undefined}
        />
      </div>
    );
  }

  return (
    <div className={`container ${isArabic ? "rtl" : ""}`} dir={isArabic ? "rtl" : "ltr"} lang={language}>
      <div className="topbar">
        <div className="brand">
          <div className="logo" aria-hidden="true"></div>
          <div className="title">
            <h1>{t("منصة مكتب الجبرية الاستشاري", "Al-Jabriya Consulting Platform")}</h1>
            <p>
              {t(
                "استبيان معلومات — البيانات الفنية والتشغيلية للمشروع",
                "Survey — Project Technical and Operational Data"
              )}
            </p>
          </div>
        </div>

        <div className="lang-toggle" aria-label="Language toggle">
          <button
            type="button"
            id="langToggleAr"
            className={isArabic ? "active" : ""}
            onClick={() => setLanguage("ar")}
          >
            AR
          </button>
          <button
            type="button"
            id="langToggleEn"
            className={!isArabic ? "active" : ""}
            onClick={() => setLanguage("en")}
          >
            EN
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>{t("نموذج الاستبيان الفني والتشغيلي", "Technical and Operational Survey Form")}</h2>
          <p>
            {t(
              "يرجى تعبئة المعلومات المتاحة بدقة، فاكتمال البيانات يساعد على بناء الدراسة الفنية والتشغيلية بصورة أفضل.",
              "Please provide the available information accurately to support a stronger technical and operational study."
            )}
          </p>
        </div>

        <div className="card-body">
          <div id="errorBox" style={{ display: errorMessage ? "block" : "none" }}>
            {errorMessage}
          </div>
          <div id="thankYouMessage" style={{ display: successMessage ? "block" : "none" }}>
            {successMessage}
          </div>

          <form id="surveyForm" encType="multipart/form-data" onSubmit={handleSubmit}>
            {sections.map((section) => (
              <div className="section" key={section.ar}>
                <h3>{t(section.ar, section.en)}</h3>
                <div className="grid">{section.questions.map(renderQuestion)}</div>
              </div>
            ))}

            {generationStatus && (
              <div className="generation-status" role="status" aria-live="polite">
                <div className="generation-spinner" aria-hidden="true"></div>
                <div className="generation-copy">
                  <strong>{t("جارٍ توليد الدراسة", "Generating study")}</strong>
                  <span>
                    {t(
                      "قد يستغرق إعداد الدراسة من 15 إلى 30 دقيقة. لا تغلق الصفحة إذا أردت استلام الملف مباشرة.",
                      "Preparing the study may take approximately 15 to 30 minutes. Keep this page open if you want to receive the file directly."
                    )}
                  </span>
                  <small>
                    {t("الوقت المنقضي: ", "Elapsed time: ")}
                    {formatGenerationTime(generationStatus.startedAt)}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary generation-cancel"
                  onClick={handleCancelGeneration}
                >
                  {t("إلغاء", "Cancel")}
                </button>
              </div>
            )}

            <div className="actions">
              <button id="generateBtn" type="submit" className="btn btn-primary" disabled={isGenerating}>
                {isGenerating
                  ? t("جارٍ توليد الدراسة...", "Generating study...")
                  : t("توليد الدراسة", "Generate Study")}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={exportSavedWord}
                disabled={isGenerating}
              >
                {t("إعادة توليد Word", "Regenerate Word")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TechnicalSurvey;
